import * as THREE from 'three';
import { AudioManager } from '../../core/AudioManager';
import { AudioHandle } from '../../core/AudioManager.types';
import { Car } from '../Car';

export class MotorAudio {
    static readonly PRELOAD = [
        '../assets/sounds/motor_start.mp3',
        '../assets/sounds/motor_idle.mp3',
        '../assets/sounds/motor_drive.wav',
    ] as const;

    private static readonly MAX_SPEED = 22;
    private static readonly BASE_RATE = 0.8;
    private static readonly MAX_RATE = 2.2;
    private static readonly START_DURATION = 1800;
    private static readonly REF_DISTANCE = 8;

    private startHandle: AudioHandle | null = null;
    private idleHandle: AudioHandle | null = null;
    private driveHandle: AudioHandle | null = null;
    private loopsActive = false;
    private startTimeout: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private readonly audioManager: AudioManager,
        private readonly car: Car,
    ) {}

    public start(): void {
        this.startHandle = this.audioManager.playPositional(
            MotorAudio.PRELOAD[0], this.car, { volume: 1, loop: false, refDistance: MotorAudio.REF_DISTANCE },
        );
        this.startTimeout = setTimeout(() => {
            this.startTimeout = null;
            this.idleHandle = this.audioManager.playPositional(
                MotorAudio.PRELOAD[1], this.car, { volume: 0, loop: true, refDistance: MotorAudio.REF_DISTANCE },
            );
            this.driveHandle = this.audioManager.playPositional(
                MotorAudio.PRELOAD[2], this.car, { volume: 0, loop: true, refDistance: MotorAudio.REF_DISTANCE },
            );
            this.loopsActive = true;
        }, MotorAudio.START_DURATION);
    }

    public stop(): void {
        if (this.startTimeout !== null) {
            clearTimeout(this.startTimeout);
            this.startTimeout = null;
        }
        if (this.startHandle) { this.audioManager.stopPositional(this.startHandle); this.startHandle = null; }
        if (this.idleHandle) { this.audioManager.stopPositional(this.idleHandle); this.idleHandle = null; }
        if (this.driveHandle) { this.audioManager.stopPositional(this.driveHandle); this.driveHandle = null; }
        this.loopsActive = false;
    }

    public update(speed: number): void {
        if (!this.loopsActive || !this.idleHandle || !this.driveHandle) { return; }

        const t = THREE.MathUtils.clamp(Math.abs(speed) / MotorAudio.MAX_SPEED, 0, 1);

        this.idleHandle.audio.setVolume(THREE.MathUtils.lerp(1, 0, t));
        this.driveHandle.audio.setVolume(THREE.MathUtils.lerp(0, 1, t));

        const rate = THREE.MathUtils.lerp(MotorAudio.BASE_RATE, MotorAudio.MAX_RATE, t);
        const driveSource = (this.driveHandle.audio as any).source;
        if (driveSource) { driveSource.playbackRate.value = rate; }
    }
}
